import { useMemo } from 'react';

import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { useSelector } from 'react-redux';

import { useAccount } from '@/hooks/useAccount';
import { LogSessionContent, LogSessionsPreview } from '@/hooks/useLogs';
import { selectChannelUserId, selectSessionId } from '@/redux/session/selectors';
import { LogsQueryVariables } from '@/shared/types/analytics';
import { getAnalyticsRangeLocal } from '@/util/analytics';
import { formatDate, addDaysToDate } from '@/util/dateUtils';
import { capitalizeFirstLetter } from '@/util/util';

interface Avatar {
  author_type?: 'brain' | 'visitor' | 'agent';
  src?: string;
}

const DEFAULT_RETENTION_DAYS = 30;

const SESSIONS_PREVIEW = gql`
  query ListRelatedSessions(
    $accountId: uuid
    $channelUserId: String
    $endDate: timestamp
    $startDate: timestamp
    $limit: Int
    $offset: Int
  ) {
    rows: list_sessions(
      args: {
        account_id: $accountId
        channel_user_id: $channelUserId
        start_time: $startDate
        end_time: $endDate
        row_limit: $limit
        row_offset: $offset
      }
    ) {
      session_id
    }
  }
`;

export const SESSION_CONTENT = gql`
  query GetSessionContent($sessionId: String) {
    rows: get_session_content(args: { session_id: $sessionId }) {
      channel
      messages
      participated_agents
    }
  }
`;

export const useRelatedConversations = (sessionId?: string, enabled = true) => {
  const channelUserId = useSelector(selectChannelUserId);
  const { account } = useAccount();
  const currentSessionId = useSelector(selectSessionId);

  const dateRange = useMemo(() => {
    const now = new Date();
    const retentionDays = account?.max_log_retention ?? DEFAULT_RETENTION_DAYS;
    const earliest = addDaysToDate(now, -retentionDays);

    return getAnalyticsRangeLocal(
      formatDate(earliest, 'yyyy-MM-dd'),
      formatDate(now, 'yyyy-MM-dd')
    );
  }, [account?.max_log_retention]);

  const { data: relatedConversations, loading } = useQuery<LogSessionsPreview, LogsQueryVariables>(
    SESSIONS_PREVIEW,
    {
      variables: {
        channelUserId,
        accountId: account?.account_id,
        ...dateRange,
        limit: 25,
        offset: 0,
      },
      skip: !enabled || !channelUserId || !account?.account_id,
    }
  );

  const { data: sessionContent, loading: sessionContentLoading } = useQuery<
    LogSessionContent,
    LogsQueryVariables
  >(SESSION_CONTENT, { variables: { sessionId }, skip: !sessionId });

  // Pick only messages events
  const messages =
    sessionContent?.rows[0]?.messages?.filter((row) => {
      return (
        row?.event === 'message:send' ||
        row?.event === 'message:brain_send' ||
        row?.event === 'message:received'
      );
    }) ?? [];

  // Get last message to show in preview
  const lastMessage = messages[messages?.length - 1];

  // Type guard to get text from last message
  const text = lastMessage?.message as {
    text?: string;
    responses?: { text?: string; type?: string }[];
    attachments?: { type?: string }[];
  };

  const relatedConversation = useMemo(() => {
    if (!lastMessage) {
      return null;
    }
    const avatar: Avatar = {};
    let message = '';

    if (text?.text) {
      message = text?.text;
    } else if (text?.responses) {
      message = text?.responses[0]?.text ?? text?.responses[0]?.type;
    } else if (text?.attachments) {
      message = capitalizeFirstLetter(text?.attachments[0]?.type);
    }

    // Format avatar regarding the origin of the last message
    if (lastMessage?.brain_id || lastMessage?.brain_parent_id) {
      avatar.author_type = 'brain';
    } else if (lastMessage?.agent_id) {
      avatar.author_type = 'agent';
      avatar.src = lastMessage?.agent_id;
    } else {
      avatar.author_type = 'visitor';
    }

    return {
      avatar,
      channel: sessionContent?.rows[0]?.channel,
      participated_agents: sessionContent?.rows[0]?.participated_agents ?? [],
      last_message: message,
      last_message_at: lastMessage?.time,
    };
  }, [lastMessage, text, sessionContent?.rows]);

  return {
    relatedConversations: relatedConversations?.rows?.filter(
      (row) => row.session_id !== currentSessionId
    ),
    relatedConversation,
    loading,
    sessionContentLoading,
  };
};
